import React from 'react';
import { Flag, CheckCircle, XCircle, Calendar } from 'lucide-react';
import { ReportSkeleton } from './SkeletonLoaders';
import { getStatusColor, getPriorityColor, formatText } from '../../utils/adminUtils';

const ReportsTab = ({ 
  filteredReports, 
  isLoading, 
  viewMode, 
  onReportSelect, 
  onReportAction 
}) => {
  if (isLoading) {
    return (
      <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
        {[...Array(6)].map((_, index) => (
          <ReportSkeleton key={index} isGrid={viewMode === 'grid'} />
        ))}
      </div>
    );
  }

  if (filteredReports.length === 0) {
    return (
      <div className="text-center py-12">
        <Flag className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No reports found</h3>
        <p className="mt-1 text-sm text-gray-500">
          Try adjusting your search terms or filters.
        </p>
      </div>
    );
  }

  return (
    <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
      {filteredReports.map((report) => (
        <div 
          key={report.id} 
          className="bg-white rounded-lg shadow-sm border p-6 cursor-pointer hover:shadow-md hover:border-gray-300 transition-all duration-200"
          onClick={() => onReportSelect(report)}
        >
          <div className={`flex ${viewMode === 'grid' ? 'flex-col space-y-4' : 'items-start justify-between'}`}>
            <div className="flex-1">
              {/* Status and priority badges */}
              <div className={`flex items-center ${viewMode === 'grid' ? 'justify-center flex-wrap' : ''} space-x-3 mb-3`}>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(report.priority)}`}>
                  {(report.priority || 'medium').toUpperCase()}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                  {(report.status || 'submitted').replace('_', ' ').toUpperCase()}
                </span>
                <span className="text-gray-500 text-sm">
                  {new Date(report.created_at).toLocaleDateString()}
                </span>
              </div>

              {/* Report title and description */}
              <h3 className={`text-lg font-semibold text-gray-900 mb-2 ${viewMode === 'grid' ? 'text-center' : ''}`}>
                {report.reason}
              </h3>
              <p className={`text-gray-600 mb-3 ${viewMode === 'grid' ? 'text-center text-sm' : ''}`}>
                {viewMode === 'grid' ? formatText(report.description) : report.description}
              </p>

              {/* Report details */}
              <div className={`text-sm text-gray-500 ${viewMode === 'grid' ? 'text-center' : ''}`}>
                <p>Target: <span className="font-medium">{report.reported_user?.full_name || report.reported_job?.title || 'N/A'}</span></p>
                <p>Reported by: <span className="font-medium">{report.reporter?.full_name || 'Anonymous'}</span></p>
                {report.evidence && report.evidence.length > 0 && (
                  <p>Evidence: {report.evidence.length} file(s)</p>
                )}
                {viewMode === 'grid' && (
                  <div className="mt-3 flex items-center justify-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>Created {new Date(report.created_at).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action buttons */}
            {report.status === 'submitted' && (
              <div className={`min-h-40 flex items-center space-x-2 ${viewMode === 'grid' ? 'mt-4 justify-center' : 'ml-4'}`}>
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card click when clicking action buttons
                    onReportAction(report.id, 'resolve', 'Quick resolve from list');
                  }}
                  className={`flex gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm font-medium ${viewMode === 'grid' ? 'flex items-center space-x-1' : ''}`}
                  title="Resolve Report"
                >
                  <CheckCircle className="w-4 h-4" />
                  {viewMode === 'list' && <span>Resolve</span>}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card click when clicking action buttons
                    onReportAction(report.id, 'dismiss', 'Quick dismiss from list');
                  }}
                  className={`flex gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium ${viewMode === 'grid' ? 'flex items-center space-x-1' : ''}`}
                  title="Dismiss Report"
                >
                  <XCircle className="w-4 h-4" />
                  {viewMode === 'list' && <span>Dismiss</span>}
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ReportsTab;
